import type { IconGlyph } from '@ipfabric/ui-components/Icon/IconGlyph';
import { MenuItem } from '@ipfabric/ui-components/MenuItem/MenuItem';
import { MenuItemsColumn } from '@ipfabric/ui-components/MenuItem/MenuItemsColumn';
import { SegmentButton } from '@ipfabric/ui-components/SegmentedButton/SegmentButton/SegmentButton';
import { SegmentedButton } from '@ipfabric/ui-components/SegmentedButton/SegmentedButton';
import type { TabVariant } from '@ipfabric/ui-components/Tab/Tab.types';
import { Tabs } from '@ipfabric/ui-components/Tabs/Tabs';
import { safeAbsurd } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import type { RequiredBy } from '@ipfabric/utils/types/RequiredBy';
import classNames from 'classnames';
import { useLocale } from 'containers/App/LocaleContext';
import type { RoutesTreeItem } from 'containers/App/Routes/RoutesTreeItem';
import { PermissionsRequiredPage } from 'pages/PermissionsRequired/PermissionsRequiredPage';
import { type FC, type ReactNode, useMemo } from 'react';
import { Navigate } from 'react-router';
import {
  Outlet,
  type RouteObject,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import type { AppTranslations } from 'translations/translations';
import type { PermissionScope } from 'utils/hooks/usePermissionScope';
import { useApiScopeRequirementMet } from 'utils/permissions/useApiScopeRequirementMet';

export type TableTabRoute = RequiredBy<RoutesTreeItem, 'path'> & {
  getTabLabel: (msg: AppTranslations) => string;
  /**
   * Specify list of permissions needed to show given tab
   *
   * You still have to guard permissions in the `element` component. That is because this
   * component renders child routes by using `<Outlet>` and is not able to inject the permission
   * to the subroute automatically there.
   */
  permission?: PermissionScope | PermissionScope[];
  default?: boolean;
  apiEndpoint?: string;
  /** Leading icon, rendered by the `top` layout's `Tabs`. */
  icon?: IconGlyph;
  /** Trailing badge node, rendered by the `top` layout's `Tabs`. */
  badge?: ReactNode;
};

const isTableTabRoute = (route?: RouteObject | null): route is TableTabRoute =>
  !!route && 'getTabLabel' in route;

type TabLayout = 'top' | 'top-nested' | 'side';

interface Props {
  className?: string;
  /**
   * Provide path "with no tab open" to be able to redirect to default tab in that case
   *
   * Note: when some nested tab has the same path (e.g. /inventory/devices
   * is root for the tabs + one particular tab too), it is automatically default and cannot
   * be changed then since there is no "default child path".
   */
  rootPath?: string;
  layout?: TabLayout;
  /** Tab style for the `top` layout's `Tabs` (default `'inner'`). */
  variant?: TabVariant;
  /** Description for side layout menu items keyed by path */
  sideMenuDescription?: Record<string, string>;
  /**
   * If enabled, tab links will preserve query string from current URL
   */
  preserveQueryString?: boolean;
  tabs: TableTabRoute[];
}

const reduceRouteDescendantPaths = (
  acc: string[],
  childRoute: RouteObject,
): string[] => {
  if (childRoute.path !== undefined) {
    acc.push(childRoute.path);
  }
  if (childRoute.children) {
    childRoute.children.reduce(reduceRouteDescendantPaths, acc);
  }
  return acc;
};

const getTabPathWithAllSubPaths = (tab: TableTabRoute): string[] => [
  tab.path,
  ...(tab.children ?? []).reduce(reduceRouteDescendantPaths, []),
];

export const TabbedTables: FC<Props> = (props) => {
  const msg = useLocale();
  const isApiScopeRequirementMet = useApiScopeRequirementMet();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isTabAvailable = (tab: RoutesTreeItem): boolean => {
    if (isTableTabRoute(tab) && tab.requiredApiScope) {
      return isApiScopeRequirementMet(tab.requiredApiScope);
    }
    if (tab.apiEndpoint) {
      return isApiScopeRequirementMet({
        method: 'POST',
        path: tab.apiEndpoint,
      });
    }
    if ((tab.children?.length ?? 0) > 0) {
      return !!tab.children?.some(isTabAvailable);
    }
    // neither a table nor nested tabs
    return true;
  };

  const hasSomeChildSamePath = useMemo(
    () => props.tabs.some((tab) => tab.path === props.rootPath),
    [props.tabs, props.rootPath],
  );

  const filteredTabs = props.tabs.filter(isTabAvailable);

  const createLink = (path: string) =>
    `${path}${props.preserveQueryString ? window.location.search : ''}`;

  const selectedTab: string | null = useMemo(
    () =>
      filteredTabs.find((tab) =>
        getTabPathWithAllSubPaths(tab).some((route) => pathname === route),
      )?.path ?? null,
    [filteredTabs, pathname],
  );

  //
  // Premature returns
  //

  if (filteredTabs.length === 0) {
    return <PermissionsRequiredPage />;
  }

  // When at root path of tabs, redirect to the default tab.
  if (
    // not applicable where some child shares root path, see comment of props.rootTab.
    !hasSomeChildSamePath &&
    props.rootPath?.replace(/\/$/, '') === pathname.replace(/\/$/, '')
  ) {
    const defaultChildUrl = createLink(
      // Fixed noUncheckedIndexedAccess NIM-19408
      filteredTabs.find(prop('default'))?.path ?? filteredTabs[0]!.path,
    );
    return <Navigate to={defaultChildUrl} replace />;
  }

  // Just show the table on a standalone page without tab controls.
  if (searchParams.get('notabs')) {
    return <Outlet />;
  }

  //
  // Main template return
  //

  const layout = props.layout ?? 'top';

  switch (layout) {
    case 'top': {
      return (
        <div className={classNames('flex flex-1 flex-col', props.className)}>
          <Tabs
            className={classNames({ mb: props.variant !== 'outer' })}
            variant={props.variant ?? 'inner'}
            tabs={filteredTabs.map((tab) => ({
              id: tab.path,
              label: tab.getTabLabel(msg),
              icon: tab.icon,
              badge: tab.badge,
            }))}
            value={selectedTab}
            onChange={(tab) => {
              navigate(createLink(tab));
            }}
          />
          <Outlet />
        </div>
      );
    }
    case 'top-nested': {
      return (
        <div className={classNames('flex flex-1 flex-col', props.className)}>
          <SegmentedButton.Single
            className='mb'
            value={selectedTab}
            onChange={(tab) => {
              navigate(createLink(tab));
            }}
          >
            {filteredTabs.map((tab) => (
              <SegmentButton key={tab.path} value={tab.path}>
                {tab.getTabLabel(msg)}
              </SegmentButton>
            ))}
          </SegmentedButton.Single>
          <Outlet />
        </div>
      );
    }
    case 'side': {
      return (
        <div
          className={classNames('flex flex-1 flex-row gap', props.className)}
        >
          <MenuItemsColumn className='flex-[0_0_268px]'>
            {filteredTabs.map((tab) => (
              <MenuItem
                key={tab.path}
                label={tab.getTabLabel(msg)}
                description={props.sideMenuDescription?.[tab.path]}
                selected={tab.path === selectedTab}
                onClick={() => {
                  navigate(createLink(tab.path));
                }}
              />
            ))}
          </MenuItemsColumn>
          <div className='flex flex-col flex-1'>
            <Outlet />
          </div>
        </div>
      );
    }
    default: {
      safeAbsurd(layout);
      return 'Unknown layout';
    }
  }
};
